/* eslint-disable react-hooks/exhaustive-deps */
import {useCallback, useContext, useEffect, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import {atomButtonDesigns, atomButtonShapes} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import MoleculeSelectPopover, {
  selectPopoverOverlayTypes,
  selectPopoverShapes
} from '@s-ui/react-molecule-select-popover'
import {useDelayToNextFrame} from '@adv-mt/mt-hook-use-delay-to-next-frame'
import {useSearchTags} from '@adv-mt/mt-search-tags'
import ChevronDown from '@adv-mt/mt-svg-icons/lib/ChevronDown'

import FiltersGroupedByAccordionsExperiment from './experiments/FiltersGroupedByAccordionsExperiment/index.js'
import useFilters from './hooks/useFilters.js'
import {directions, FILTERS_PLACES} from './config.js'
import {useSearchFilters} from './hook-use-search-filters.js'
import {getFilterComponentBy, getSearchFromUpdatedFields} from './list-filters-helpers.js'

const ListFilters = ({
  aggregations,
  config: customFiltersConfig = [],
  direction = directions.column,
  filtersProps = {},
  hasUserInteractionWithFilters = false,
  isAdsSearchLite,
  isLoading,
  isSearchSidebar = false,
  isSearchTopbar = false,
  listingSectionId,
  onCloseFilter = () => {},
  onFilter = () => {},
  onOpenFilter = () => {},
  overlayContentRef,
  pageName = '',
  place,
  search,
  withLabels = true
}) => {
  const [forceClosePopover, setForceClosePopover] = useState(false)
  const [currentSearch, setCurrentSearch] = useState(search)

  const isFiltersGroupedByAccordionsExperimentActive =
    place === FILTERS_PLACES.SIDEBAR || place === FILTERS_PLACES.MODAL

  const {getFilterConfigByPlace} = useFilters({listingSectionId, place, hasInstalment: currentSearch?.hasInstalment})

  useEffect(() => {
    setCurrentSearch(search)
  }, [search])

  const delayToNextFrame = useDelayToNextFrame()

  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const APP_NAME = config.get('APP_NAME')
  const APP_NAME_COCHES = config.get('APP_NAME_COCHES')
  const APP_NAME_MOTOS = config.get('APP_NAME_MOTOS')
  const isMobile = config.get('isMobile')
  const {BODY_TYPE_CATEGORY, DEFAULT_FILTERS_PLACEHOLDERS, DEFAULT_FILTERS, FILTER_NAMES} = config.get('search')
  const {FILTERS_GROUPS_IDS, FILTERS_BY_GROUPS_IDS} = config.get('filtersGroups')
  const {FILTERS_LABELS} = config.get('filtersLabels')
  const {FILTERS_OPTIONS} = config.get('filtersOptions')
  const {FILTERS_PLACEHOLDERS} = config.get('filtersPlaceholders')

  const {categoryId, offerTypeId, filteredOfferTypeId} = currentSearch
  const searchTags = useSearchTags({search: currentSearch, pageName})

  const baseClasses = cx('mt-ListFilters', `mt-ListFilters--${direction}`, {
    'mt-ListFilters--lite default': isAdsSearchLite
  })

  const showSelectPopoverFilterWrapper = (isSearchTopbar && direction === directions.row) || isAdsSearchLite

  const iconChevronDown = useCallback(() => <ChevronDown />, [])

  const filtersList = useSearchFilters({
    categoryId,
    customFiltersConfig,
    filteredOfferTypeId,
    listingSectionId,
    offerTypeId,
    pageName
  })

  const _handleChange = (
    _,
    {field, value, lastFilter, withFilterAddedEvent = true, forceClosePopover = true, extraSearch}
  ) => {
    const nextSearch = {
      ...getSearchFromUpdatedFields({
        BODY_TYPE_CATEGORY,
        FILTER_NAMES,
        pageName,
        search: currentSearch,
        field,
        value
      }),
      // Fields that must change together with `field` in a single search update
      ...extraSearch
    }

    const isMultiSelectFilter = Array.isArray(value)

    setCurrentSearch(nextSearch)
    delayToNextFrame(() => {
      onFilter({
        search: nextSearch,
        lastFilter,
        withFilterAddedEvent
      })
    })

    const closePopover = forceClosePopover && showSelectPopoverFilterWrapper && !isMultiSelectFilter

    closePopover && setForceClosePopover(true)
  }

  const handleCloseFilter = () => {
    setForceClosePopover(true)
    delayToNextFrame(() => {
      onCloseFilter()
    })
  }

  const handleOpenFilter = () => {
    onOpenFilter()
    setForceClosePopover(false)
  }
  const handleRemoveFilter = ({filterName}) => {
    if (Object.values(FILTERS_GROUPS_IDS).includes(filterName)) {
      FILTERS_BY_GROUPS_IDS[filterName].forEach(groupField => {
        _handleChange('', {field: groupField, value: DEFAULT_FILTERS[groupField]})
      })
    } else {
      _handleChange('', {field: filterName, value: DEFAULT_FILTERS[filterName]})
    }
  }

  const getVehicleAggregations = () =>
    aggregations?.filter(
      ({filterName: aggregationFilterName}) =>
        aggregationFilterName === FILTER_NAMES.MAKE || aggregationFilterName === FILTER_NAMES.MODEL
    )

  const getLocationAggregations = () =>
    aggregations?.find(({filterName: aggregationFilterName}) => aggregationFilterName === FILTER_NAMES.PROVINCE)?.items

  const getAggreationsByFilterName = useCallback(
    ({filterName}) => {
      if (filterName === FILTER_NAMES.VEHICLES) return getVehicleAggregations()
      if (filterName === FILTER_NAMES.LOCATION) return getLocationAggregations()
      return aggregations?.find(({filterName: aggregationFilterName}) => aggregationFilterName === filterName)?.items
    },
    [aggregations]
  )

  const renderUI = () => {
    const filterComponents = filtersList?.filter(Boolean).map(filterName => ({
      ...filtersProps[filterName],
      ...getFilterConfigByPlace({filterName}),
      aggregations: getAggreationsByFilterName({filterName}),
      component: getFilterComponentBy({
        FILTERS_GROUPS_IDS,
        FILTER_NAMES,
        filterName,
        isAdsSearchLite,
        isCoches: APP_NAME === APP_NAME_COCHES,
        isMobile,
        isMotos: APP_NAME === APP_NAME_MOTOS,
        isSearchSidebar,
        isSearchTopbar,
        pageName
      }),
      initialSearch: currentSearch,
      isLoading,
      listingSectionId,
      name: filterName,
      onChange: _handleChange,
      overlayContentRef,
      pageName,
      value: currentSearch[filterName],
      // Experiment abtest1_web_buyer_filters_grouped_by_accordions_desktop
      // Experiment abtest2_web_buyer_filters_grouped_by_accordions_mobile
      isFiltersGroupedByAccordionsExperimentActive
    }))

    // Experiment abtest1_web_buyer_filters_grouped_by_accordions_desktop
    // Experiment abtest2_web_buyer_filters_grouped_by_accordions_mobile
    if (isFiltersGroupedByAccordionsExperimentActive) {
      return (
        <FiltersGroupedByAccordionsExperiment
          filters={filterComponents}
          listingSectionId={listingSectionId}
          overlayContentRef={overlayContentRef}
          searchTags={searchTags}
        />
      )
    }

    return filterComponents.map(
      ({
        aggregations,
        component: FilterComponent,
        initialSearch,
        name: filterName,
        onChange,
        overlayContentRef,
        value
      }) => {
        // First step to implement the new filters refactor
        if (
          FilterComponent &&
          (place === FILTERS_PLACES.SIDEBAR || place === FILTERS_PLACES.MODAL || place === FILTERS_PLACES.LITE)
        ) {
          const {type, wrapper} = getFilterConfigByPlace({filterName})
          return (
            <FilterComponent
              aggregations={aggregations}
              hasUserInteractionWithFilters={hasUserInteractionWithFilters}
              initialSearch={initialSearch}
              isLoading={isLoading}
              key={filterName}
              label={FILTERS_LABELS[filterName]}
              listingSectionId={listingSectionId}
              name={filterName}
              options={FILTERS_OPTIONS[filterName]}
              onChange={onChange}
              overlayContentRef={overlayContentRef}
              pageName={pageName}
              placeholder={FILTERS_PLACEHOLDERS[filterName]}
              type={type}
              value={value}
              withLabel={withLabels}
              wrapper={wrapper}
              {...filtersProps[filterName]}
              // Experiment abtest1_web_buyer_filters_grouped_by_accordions_desktop
              // Experiment abtest2_web_buyer_filters_grouped_by_accordions_mobile
              isFiltersGroupedByAccordionsExperimentActive={isFiltersGroupedByAccordionsExperimentActive}
            />
          )
        }

        let filterFieldName = filterName

        const selectText =
          ((isSearchTopbar || isAdsSearchLite) &&
            searchTags
              .filter(({field}) => {
                if (field === FILTER_NAMES.BODY_TYPE) {
                  filterFieldName = FILTER_NAMES.BODY_TYPE
                  return filterName === FILTER_NAMES.BODY_TYPE_CATEGORY
                }

                if (Object.values(FILTERS_GROUPS_IDS).includes(filterName)) {
                  return FILTERS_BY_GROUPS_IDS[filterName].includes(field)
                }

                return field === filterName
              })
              .map(({label}) => label)
              .join(', ')) ||
          i18n.t(DEFAULT_FILTERS_PLACEHOLDERS[filterName], 1)

        const getTempSelectText = () => {
          switch (filterName) {
            case FILTER_NAMES.MAKE:
              return initialSearch.make || i18n.t(DEFAULT_FILTERS_PLACEHOLDERS[filterName], 1)
            case FILTER_NAMES.MODEL:
              return initialSearch.model?.name || i18n.t(DEFAULT_FILTERS_PLACEHOLDERS[filterName], 1)
            default:
              return selectText
          }
        }

        // Temp: needed to prevent unexpected behavir until multimake filter was implemented
        const tempSelectText = getTempSelectText()

        const [firstVehicle] = initialSearch[FILTER_NAMES.VEHICLES] || []
        const isFilterModel = filterName === FILTER_NAMES.MODEL
        const hasSelectedMake = Boolean(firstVehicle?.[FILTER_NAMES.MAKE])
        const isDisabled = isFilterModel && !hasSelectedMake

        return (
          FilterComponent &&
          (showSelectPopoverFilterWrapper ? (
            <div className="mt-ListFilters-popover" key={filterName}>
              <MoleculeSelectPopover
                acceptButtonText={i18n.t('COMMON.ACCEPT')}
                acceptButtonOptions={{
                  shape: atomButtonShapes.CIRCULAR
                }}
                cancelButtonText={i18n.t('COMMON.DELETE_FILTERS')}
                cancelButtonOptions={{
                  disabled: tempSelectText === i18n.t(DEFAULT_FILTERS_PLACEHOLDERS[filterName], 1),
                  shape: atomButtonShapes.CIRCULAR,
                  design: atomButtonDesigns.FLAT
                }}
                forceClosePopover={forceClosePopover}
                isDisabled={isDisabled}
                iconArrowDown={iconChevronDown}
                onAccept={handleCloseFilter}
                onCancelButtonClick={() => handleRemoveFilter({filterName: filterFieldName})}
                onClose={handleCloseFilter}
                onOpen={handleOpenFilter}
                overlayContentRef={overlayContentRef}
                overlayType={selectPopoverOverlayTypes.LIGHT}
                selectText={tempSelectText}
                shape={selectPopoverShapes.CIRCULAR}
                title={tempSelectText}
                {...(isAdsSearchLite && {fullWidth: true})}
              >
                <div className="mt-ListFilters-popoverContent">
                  <FilterComponent
                    {...filtersProps[filterName]}
                    aggregations={aggregations}
                    initialSearch={initialSearch}
                    isLoading={isLoading}
                    listingSectionId={listingSectionId}
                    onChange={_handleChange}
                    pageName={pageName}
                    value={value}
                    withLabel={false}
                    // Experiment
                    hasUserInteractionWithFilters={hasUserInteractionWithFilters}
                    // Experiment abtest1_web_buyer_filters_grouped_by_accordions_desktop
                    // Experiment abtest2_web_buyer_filters_grouped_by_accordions_mobile
                    isFiltersGroupedByAccordionsExperimentActive={isFiltersGroupedByAccordionsExperimentActive}
                  />
                </div>
              </MoleculeSelectPopover>
            </div>
          ) : (
            <FilterComponent
              {...filtersProps[filterName]}
              aggregations={aggregations}
              initialSearch={initialSearch}
              key={filterName}
              isLoading={isLoading}
              listingSectionId={listingSectionId}
              onChange={_handleChange}
              overlayContentRef={overlayContentRef}
              pageName={pageName}
              value={value}
              // Experiment
              hasUserInteractionWithFilters={hasUserInteractionWithFilters}
              // Experiment abtest1_web_buyer_filters_grouped_by_accordions_desktop
              // Experiment abtest2_web_buyer_filters_grouped_by_accordions_mobile
              isFiltersGroupedByAccordionsExperimentActive={isFiltersGroupedByAccordionsExperimentActive}
            />
          ))
        )
      }
    )
  }

  return <div className={baseClasses}>{renderUI()}</div>
}

ListFilters.displayName = 'ListFilters'

ListFilters.propTypes = {
  aggregations: PropTypes.array,
  config: PropTypes.arrayOf(
    PropTypes.shape({
      component: PropTypes.node,
      name: PropTypes.string
    })
  ),
  direction: PropTypes.oneOf(Object.values(directions)),
  filtersProps: PropTypes.object,
  hasUserInteractionWithFilters: PropTypes.bool,
  isAdsSearchLite: PropTypes.bool,
  isLoading: PropTypes.bool,
  isSearchSidebar: PropTypes.bool,
  isSearchTopbar: PropTypes.bool,
  listingSectionId: PropTypes.string,
  onCloseFilter: PropTypes.func,
  onFilter: PropTypes.func.isRequired,
  onOpenFilter: PropTypes.func,
  overlayContentRef: PropTypes.object,
  pageName: PropTypes.string,
  place: PropTypes.string,
  search: PropTypes.object.isRequired,
  withLabels: PropTypes.bool
}

export {directions as listFiltersDirections, FILTERS_PLACES as listFiltersPlaces}

export default ListFilters
